import React from "react"

import { Typography } from "antd"
import cls from "classnames"

import shopify_already_a_chargeback_step1 from "../../../../Images/credit-requests/instructions/shopify_already_a_chargeback_step1.png"
import shopify_already_a_chargeback_step2 from "../../../../Images/credit-requests/instructions/shopify_already_a_chargeback_step2.png"
import shopify_already_a_chargeback_step3 from "../../../../Images/credit-requests/instructions/shopify_already_a_chargeback_step3.png"
import shopify_already_a_chargeback_step4 from "../../../../Images/credit-requests/instructions/shopify_already_a_chargeback_step4.png"
import shopify_failed_transaction_step1 from "../../../../Images/credit-requests/instructions/shopify_failed_transaction_step1.png"
import shopify_failed_transaction_step2 from "../../../../Images/credit-requests/instructions/shopify_failed_transaction_step2.png"
import shopify_failed_transaction_step3 from "../../../../Images/credit-requests/instructions/shopify_failed_transaction_step3.png"
import shopify_failed_transaction_step4 from "../../../../Images/credit-requests/instructions/shopify_failed_transaction_step4.png"
import shopify_not_found from "../../../../Images/credit-requests/instructions/shopify_not_found.png"
import shopify_previously_refunded_step1 from "../../../../Images/credit-requests/instructions/shopify_previously_refunded_step1.png"
import shopify_previously_refunded_step2 from "../../../../Images/credit-requests/instructions/shopify_previously_refunded_step2.png"
import shopify_previously_refunded_step3 from "../../../../Images/credit-requests/instructions/shopify_previously_refunded_step3.png"
import shopify_previously_refunded_step4 from "../../../../Images/credit-requests/instructions/shopify_previously_refunded_step4.png"
import stripe_already_a_chargeback from "../../../../Images/credit-requests/instructions/stripe_already_a_chargeback.png"
import stripe_failed_transaction from "../../../../Images/credit-requests/instructions/stripe_failed_transaction.png"
import stripe_not_found from "../../../../Images/credit-requests/instructions/stripe_not_found.png"
import stripe_previously_refunded from "../../../../Images/credit-requests/instructions/stripe_previously_refunded.png"
import { BulletsList } from "./components/BulletsList"
import { ImagePreview } from "./components/ImagePreview"
import { Notice } from "./components/Notice"
import { NumberedListItem } from "./components/NumberedListItem"

import styles from "./CreditRequestInstructions.module.less"

const { Text, Link } = Typography

const DefaultInstruction = () => {
  return (
    <div className={cls(styles.instructionsContent, styles.defaultInstruction)}>
      <Text className={styles.title}>How to get screenshots from your payment processor</Text>
      <Text className={styles.description}>
        The proof you need depends on what happened to the transaction. Choose “Reason for credit
        request” and check the recomendations.
      </Text>

      <Text>
        Review the{" "}
        <Link
          href="https://help.chargeback.io/en/articles/12782870-request-manual-credits-for-alerts#h_35188e9a47"
          target="_blank"
          rel="noopener noreferrer"
        >
          full guide
        </Link>
        , or contact <Link onClick={() => window?.Intercom("show")}>Support</Link> if none of these
        options match.
      </Text>
    </div>
  )
}

const ShopifyNotFound = () => (
  <div className={cls(styles.instructionsContent, styles.defaultGetScreenshotInstructions)}>
    <Text className={styles.title}>How to get screenshots from Shopify</Text>
    <Text className={styles.description}>
      Show that no matching transaction appears after applying these filters:
    </Text>
    <BulletsList items={["Order Date", "Last 4 digits of the card", "Amount", "Currency"]} />
    <ImagePreview src={shopify_not_found} alt="Shopify not found" />
    <Notice
      title="Important notice"
      description="Make sure your screenshot shows the filtered results page with no transactions."
    />
  </div>
)

const ShopifyRefundedBeforeAlert = () => (
  <div className={cls(styles.instructionsContent)}>
    <Text className={styles.title}>How to get a report from Shopify</Text>
    <NumberedListItem number={1} text="In Shopify, open the order" />
    <ImagePreview
      src={shopify_previously_refunded_step1}
      alt="Shopify previously refunded step 1"
    />
    <NumberedListItem
      number={2}
      text="Scroll to the timeline, then click the two arrows to expand the transaction details"
    />
    <ImagePreview
      src={shopify_previously_refunded_step2}
      alt="Shopify previously refunded step 2"
    />
    <NumberedListItem number={3} text="Keep the authorization information visible" />
    <ImagePreview
      src={shopify_previously_refunded_step3}
      alt="Shopify previously refunded step 3"
    />
    <NumberedListItem
      number={4}
      text="At the top of the page, click Print, then click Print order page"
    />
    <ImagePreview
      src={shopify_previously_refunded_step4}
      alt="Shopify previously refunded step 4"
    />
  </div>
)

const ShopifyChargebackBeforeAlert = () => (
  <div className={cls(styles.instructionsContent)}>
    <Text className={styles.title}>How to get a report from Shopify</Text>
    <NumberedListItem number={1} text="In Shopify, open the order" />
    <ImagePreview
      src={shopify_already_a_chargeback_step1}
      alt="Shopify already a chargeback step 1"
    />
    <NumberedListItem
      number={2}
      text="Scroll to the timeline, then click the two arrows to expand the transaction details"
    />
    <ImagePreview
      src={shopify_already_a_chargeback_step2}
      alt="Shopify already a chargeback step 2"
    />
    <NumberedListItem number={3} text="Keep the authorization information visible" />
    <ImagePreview
      src={shopify_already_a_chargeback_step3}
      alt="Shopify already a chargeback step 3"
    />
    <NumberedListItem
      number={4}
      text="At the top of the page, click Print, then click Print order page"
    />
    <ImagePreview
      src={shopify_already_a_chargeback_step4}
      alt="Shopify already a chargeback step 4"
    />
  </div>
)

const ShopifyFailedTransaction = () => (
  <div className={cls(styles.instructionsContent)}>
    <Text className={styles.title}>How to get a report from Shopify</Text>
    <NumberedListItem number={1} text="In Shopify, open the order" />
    <ImagePreview src={shopify_failed_transaction_step1} alt="Shopify failed transaction step 1" />
    <NumberedListItem
      number={2}
      text="Scroll to the timeline, then click the two arrows to expand the transaction details"
    />
    <ImagePreview src={shopify_failed_transaction_step2} alt="Shopify failed transaction step 2" />
    <NumberedListItem number={3} text="Keep the authorization information visible" />
    <ImagePreview src={shopify_failed_transaction_step3} alt="Shopify failed transaction step 3" />
    <NumberedListItem
      number={4}
      text="At the top of the page, click Print, then click Print order page"
    />
    <ImagePreview src={shopify_failed_transaction_step4} alt="Shopify failed transaction step 4" />
  </div>
)

const StripeNotFound = () => (
  <div className={cls(styles.instructionsContent, styles.defaultGetScreenshotInstructions)}>
    <Text className={styles.title}>How to get screenshots from Stripe</Text>
    <Text className={styles.description}>
      Show that no matching transaction appears after applying these filters:
    </Text>
    <BulletsList items={["Last 4 digits of the card", "Amount", "Brand", "Date", "Currency"]} />
    <ImagePreview src={stripe_not_found} alt="Stripe not found" />
    <Notice
      title="Important notice"
      description="Make sure your screenshot shows the filtered results page with no transactions."
    />
  </div>
)

const StripeRefundedBeforeAlert = () => (
  <div className={cls(styles.instructionsContent, styles.defaultGetScreenshotInstructions)}>
    <Text className={styles.title}>How to get screenshots from Stripe</Text>
    <Text className={styles.description}>
      Show that the transaction was already refunded. Include:
    </Text>
    <BulletsList
      items={["Recent activity", "Payment details and customer information ", "Payment method "]}
    />
    <ImagePreview src={stripe_previously_refunded} alt="Stripe not found" />
    <Notice
      title="Important notice"
      description="Make sure your screenshot shows all fields highlighted on the example above."
    />
  </div>
)

const StripeChargebackBeforeAlert = () => (
  <div className={cls(styles.instructionsContent, styles.defaultGetScreenshotInstructions)}>
    <Text className={styles.title}>How to get screenshots from Stripe</Text>
    <Text className={styles.description}>
      Show that the transaction was already disputed. Include:
    </Text>
    <BulletsList
      items={[
        "Recent activity",
        "Payment details and customer information",
        "Payment method",
        "Status showing payment was disputed"
      ]}
    />
    <ImagePreview src={stripe_already_a_chargeback} alt="Stripe already a chargeback" />
    <Notice
      title="Important notice"
      description="Make sure your screenshot shows all fields highlighted on the example above."
    />
  </div>
)

const StripeFailedTransaction = () => (
  <div className={cls(styles.instructionsContent, styles.defaultGetScreenshotInstructions)}>
    <Text className={styles.title}>How to get screenshots from Stripe</Text>
    <Text className={styles.description}>Show that the transaction was declined. Include:</Text>
    <BulletsList
      items={["Recent activity", "Payment details and customer information", "Payment method"]}
    />
    <ImagePreview src={stripe_failed_transaction} alt="Stripe failed transaction" />
    <Notice
      title="Important notice"
      description="Make sure your screenshot shows all fields highlighted on the example above."
    />
  </div>
)

const OtherNotFound = () => (
  <div className={cls(styles.instructionsContent, styles.defaultGetScreenshotInstructions)}>
    <Text className={styles.title}>How to get screenshots from your payment processor</Text>
    <Text className={styles.description}>
      Show that no matching transaction appears after applying these filters:
    </Text>
    <BulletsList
      items={["Order Date", "Transaction amount", "Last 4 digits of the card", "Currency"]}
    />
    <Notice
      title="Important notice"
      description="Make sure your screenshot shows the filtered results page with no transactions."
    />
  </div>
)

const OtherRefundedBeforeAlert = () => (
  <div className={cls(styles.instructionsContent, styles.defaultGetScreenshotInstructions)}>
    <Text className={styles.title}>How to get screenshots from your payment processor</Text>
    <Text className={styles.description}>
      Show that the transaction was already refunded. Include:
    </Text>
    <BulletsList
      items={[
        "Payment status",
        "Payment timestamp",
        "Refund timestamp",
        "Payment method",
        "Billing descriptor",
        "Transaction amount"
      ]}
    />
    <Notice
      title="Important notice"
      description="Make sure your screenshot shows all fields listed above."
    />
  </div>
)

const OtherChargebackBeforeAlert = () => (
  <div className={cls(styles.instructionsContent, styles.defaultGetScreenshotInstructions)}>
    <Text className={styles.title}>How to get screenshots from your payment processor</Text>
    <Text className={styles.description}>
      Show that the transaction was already disputed. Include:
    </Text>
    <BulletsList
      items={[
        "Payment status",
        "Payment timestamp",
        "Chargeback/dispute timestamp",
        "Payment method",
        "Billing descriptor",
        "Transaction amount"
      ]}
    />
    <Notice
      title="Important notice"
      description="Make sure your screenshot shows all fields listed above."
    />
  </div>
)

const OtherFailedTransaction = () => (
  <div className={cls(styles.instructionsContent, styles.defaultGetScreenshotInstructions)}>
    <Text className={styles.title}>How to get screenshots from your payment processor</Text>
    <Text className={styles.description}>Show that the transaction was declined. Include:</Text>
    <BulletsList
      items={[
        "Payment status",
        "Payment timestamp",
        "Payment failure timestamp",
        "Payment method",
        "Billing descriptor",
        "Transaction amount"
      ]}
    />
    <Notice
      title="Important notice"
      description="Make sure your screenshot shows all fields listed above."
    />
  </div>
)

export const contentMap = {
  shopify: {
    "Transaction not found": ShopifyNotFound,
    "Transaction was refunded before the alert": ShopifyRefundedBeforeAlert,
    "Transaction became a chargeback before the alert": ShopifyChargebackBeforeAlert,
    "Alert went to failed/not authenticated transaction": ShopifyFailedTransaction,
    Other: DefaultInstruction
  },
  stripe: {
    "Transaction not found": StripeNotFound,
    "Transaction was refunded before the alert": StripeRefundedBeforeAlert,
    "Transaction became a chargeback before the alert": StripeChargebackBeforeAlert,
    "Alert went to failed/not authenticated transaction": StripeFailedTransaction,
    Other: DefaultInstruction
  },
  other: {
    "Transaction not found": OtherNotFound,
    "Transaction was refunded before the alert": OtherRefundedBeforeAlert,
    "Transaction became a chargeback before the alert": OtherChargebackBeforeAlert,
    "Alert went to failed/not authenticated transaction": OtherFailedTransaction,
    Other: DefaultInstruction
  }
}
